import { useTranslation } from 'react-i18next'

import { useCampaignRealtime } from '#/entities/campaign'
import { Button } from '#/shared/ui/button'

import { CAMPAIGN_DETAIL_TABS } from '../model/detail-tabs'
import { useCampaignActions } from '../model/use-campaign-actions'
import { useCampaignAddStep } from '../model/use-campaign-add-step'
import { useCampaignDetail } from '../model/use-campaign-detail'
import { CampaignActivityTab } from './campaign-activity-tab'
import { CampaignContactTab } from './campaign-contact-tab'
import { CampaignDetailHeaderCard } from './campaign-detail-header'
import { CampaignOverviewTab } from './campaign-overview-tab'
import { CampaignSequenceTab } from './campaign-sequence-tab'
import { CampaignDetailSkeleton } from './campaign-skeletons'

interface CampaignDetailPageProps {
  campaignId: string
}

function DetailLoadError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-10 text-center">
      <p className="text-sm text-gray-500">{t('campaigns.detail.loadError')}</p>
      <Button variant="outline" size="xs" onClick={onRetry}>
        {t('campaigns.detail.retry')}
      </Button>
    </div>
  )
}

export function CampaignDetailPage({ campaignId }: CampaignDetailPageProps) {
  useCampaignRealtime()
  const detail = useCampaignDetail(campaignId)
  const actions = useCampaignActions(campaignId)
  // The picker lives on the Sequence tab, but the header's "Add step" opens
  // it, so its state sits here — the one place both can reach.
  const addStep = useCampaignAddStep()

  if (detail.isPending) return <CampaignDetailSkeleton />
  // A resolved overview with no header is a broken campaign, not a slow one —
  // it must not sit under a skeleton that never resolves.
  if (detail.isError || detail.header == null || detail.overview == null) {
    return <DetailLoadError onRetry={() => void detail.refetch()} />
  }

  return (
    <div className="flex flex-col gap-4">
      <CampaignDetailHeaderCard
        header={detail.header}
        tabs={detail.tabs}
        activeTab={detail.activeTab}
        onTabChange={detail.onTabChange}
        actions={actions}
        onAddStep={() => {
          detail.onTabChange(CAMPAIGN_DETAIL_TABS.SEQUENCE)
          addStep.onOpen()
        }}
      />
      {detail.activeTab === CAMPAIGN_DETAIL_TABS.OVERVIEW && (
        <CampaignOverviewTab
          campaignId={campaignId}
          overview={detail.overview}
          onOpenWaiting={detail.onOpenWaiting}
        />
      )}
      {detail.activeTab === CAMPAIGN_DETAIL_TABS.SEQUENCE && (
        <CampaignSequenceTab campaignId={campaignId} addStep={addStep} />
      )}
      {detail.activeTab === CAMPAIGN_DETAIL_TABS.CONTACT && (
        <CampaignContactTab
          campaignId={campaignId}
          initialFilter={detail.contactFilter}
        />
      )}
      {detail.activeTab === CAMPAIGN_DETAIL_TABS.ACTIVITY && (
        <CampaignActivityTab campaignId={campaignId} />
      )}
    </div>
  )
}
